import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Brain, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';
import { useTenant } from '../../contexts/TenantContext';

const loginSchema = z.object({
  usernameOrEmail: z.string().min(1, 'Campo requerido'),
  password: z.string().min(1, 'Campo requerido'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const tenant = useTenant();
  const isKids = tenant.slug === 'kids';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      setIsLoading(true);
      const result = await authService.login(data);
      setUser(result.user, result.profile);
      toast.success(isKids ? '¡Bienvenido! 🎉' : '¡Bienvenido de vuelta!');
      const from = (location.state as any)?.from || '/juegos';
      navigate(from);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md glass-container" style={{ borderColor: tenant.theme.colors.primary }}>
      <div className="text-center mb-8">
        {/* Logo dinámico */}
        <div className="text-6xl mb-4 animate-bounce">
          {tenant.content.logoIcon}
        </div>

        <h1
          className="text-3xl font-bold mb-2"
          style={{
            fontFamily: tenant.theme.fonts.primary.family,
            color: tenant.theme.colors.accent,
          }}
        >
          {isKids ? '¡Hola Papá/Mamá!' : 'Iniciar sesión'}
        </h1>
        <p className="text-white/70">
          {isKids ? 'Acceso para padres' : `Bienvenido de vuelta a ${tenant.name}`}
        </p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            Usuario o Email
          </label>
          <input
            {...register('usernameOrEmail')}
            type="text"
            className="input-neureka"
            placeholder="usuario o email@ejemplo.com"
            disabled={isLoading}
          />
          {errors.usernameOrEmail && (
            <p className="text-red-400 text-sm mt-1">{errors.usernameOrEmail.message}</p>
          )}
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">
            <Lock className="w-4 h-4 inline mr-2" />
            Contraseña
          </label>
          <input
            {...register('password')}
            type="password"
            className="input-neureka"
            placeholder="••••••••"
            disabled={isLoading}
          />
          {errors.password && (
            <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
          )}
        </div>
        
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm hover:underline"
            style={{ color: tenant.theme.colors.accent }}
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 font-bold py-3 px-6 rounded-lg transition-transform hover:scale-105 disabled:opacity-50"
          style={{
            background: tenant.theme.colors.accent,
            color: isKids ? '#fff' : '#000',
          }}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {isKids ? 'Entrando...' : 'Iniciando sesión...'}
            </>
          ) : (
            isKids ? '¡Entrar!' : 'Iniciar sesión'
          )}
        </button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-white/70">
          ¿No tienes cuenta?{' '}
          <Link to="/register" className="text-neureka-yellow font-semibold hover:underline">
            Regístrate gratis
          </Link>
        </p>
      </div>
    </div>
  );
}
